"use client";

import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { TwtBrand } from "~/components/TwtBrand";
import { NewsletterSignupLink } from "~/components/newsletter/NewsletterSignupLink";
import { MemberAccessMenuItems, useMemberAccess } from "~/components/memberAccess";
import { blogHref, launchUnavailableHref, publicNavLinks } from "~/components/public-nav-links";

const accessMenuLinks = [
  { label: "Pricing", href: "/pricing" },
  { label: "Newsletter", href: launchUnavailableHref },
  { label: "Discord", href: "https://discord.com/invite/tradewithtitans" },
];

function isExternalHref(href: string) {
  return href.startsWith("http");
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function MarketingLink({
  href,
  className,
  onClick,
  children,
}: {
  href: string;
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (isExternalHref(href)) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function MarketingBrandLogo({ className = "" }: { className?: string }) {
  return (
    <TwtBrand
      size="md"
      className={cx("gap-2 sm:gap-2.5", className)}
      markClassName="h-6 w-6 text-[14px] sm:h-7 sm:w-7 sm:text-[15px]"
      textClassName="whitespace-nowrap text-[16px] sm:text-[20px] md:text-[22px]"
    />
  );
}

export function MarketingNav({ memberAccessHeld = false }: { memberAccessHeld?: boolean }) {
  const [accessOpen, setAccessOpen] = useState(false);
  const accessRef = useRef<HTMLDivElement | null>(null);
  const access = useMemberAccess();

  useEffect(() => {
    if (!accessOpen) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (
        accessRef.current &&
        event.target instanceof Node &&
        !accessRef.current.contains(event.target)
      ) {
        setAccessOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.key === "Escape" &&
        !(event.target instanceof Element && event.target.closest('[role="dialog"]'))
      ) {
        setAccessOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [accessOpen]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#e0d8c8] bg-[#f6f3ea]/95 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1360px] items-center justify-between gap-2 px-3 py-4 sm:gap-5 sm:px-6 md:px-10 md:py-5">
        <Link
          href="/"
          className="flex min-w-0 shrink-0 items-center text-[#14120f]"
          aria-label="Trade with Titans home"
        >
          <MarketingBrandLogo />
        </Link>
        <nav
          className="hidden items-center gap-8 text-[15px] font-medium leading-5 text-[#4f4638]/80 lg:flex"
          aria-label="Public navigation"
        >
          {publicNavLinks.map((link) =>
            link.label === "Newsletter" ? (
              <NewsletterSignupLink
                key={link.label}
                source="site-nav"
                className="cursor-pointer bg-transparent text-left transition hover:text-[#14120f]"
              >
                Newsletter
              </NewsletterSignupLink>
            ) : (
              <MarketingLink
                key={`${link.label}:${link.href}`}
                href={link.href}
                className="transition hover:text-[#14120f]"
              >
                {link.label}
              </MarketingLink>
            )
          )}
        </nav>
        <div className="relative shrink-0" ref={accessRef}>
          <div className="inline-flex overflow-hidden rounded-full bg-[#14120f] text-[12px] font-semibold leading-5 text-[#f6f3ea] shadow-[0_12px_35px_-24px_rgba(20,18,15,.9)] sm:text-[15px]">
            <a
              href={memberAccessHeld ? "/account" : "/dashboard"}
              className="twt-member-cta--access inline-flex items-center whitespace-nowrap px-2.5 py-2.5 transition hover:opacity-80 sm:px-5 sm:py-3 md:px-6"
            >
              Sign in
            </a>
            <a
              href={memberAccessHeld ? "/account" : "/dashboard"}
              className="twt-member-cta--dashboard items-center whitespace-nowrap px-2.5 py-2.5 transition hover:opacity-80 sm:px-5 sm:py-3 md:px-6"
              aria-label={memberAccessHeld ? "Open your account" : "Open member dashboard"}
            >
              {memberAccessHeld ? "Account" : "Dashboard"}
            </a>
            <button
              type="button"
              aria-label="More access options"
              aria-expanded={accessOpen}
              aria-haspopup="menu"
              onClick={() => setAccessOpen((open) => !open)}
              className="border-[#f6f3ea]/16 inline-flex items-center border-l px-2 py-2.5 transition hover:opacity-80 sm:px-3.5 sm:py-3 md:px-4"
            >
              <ChevronDown
                className={`transition ${accessOpen ? "rotate-180" : ""}`}
                size={16}
              />
            </button>
          </div>
          {accessOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full z-50 mt-2 w-56 rounded-2xl border border-[#d6cbb7] bg-[#fbf8ef] p-2 text-[#14120f] shadow-2xl"
            >
              <div className="mb-1 border-b border-black/10 pb-2 lg:hidden">
                <MarketingLink
                  href="/"
                  className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-black/5"
                  onClick={() => setAccessOpen(false)}
                >
                  Home
                </MarketingLink>
                <MarketingLink
                  href="/approach"
                  className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-black/5"
                  onClick={() => setAccessOpen(false)}
                >
                  Approach
                </MarketingLink>
                <MarketingLink
                  href={blogHref}
                  className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-black/5"
                  onClick={() => setAccessOpen(false)}
                >
                  Blog
                </MarketingLink>
              </div>
              {accessMenuLinks.map((link) =>
                link.label === "Newsletter" ? (
                  <NewsletterSignupLink
                    key={link.label}
                    source="site-nav-cta"
                    role="menuitem"
                    className="block w-full cursor-pointer rounded-xl bg-transparent px-3 py-2 text-left text-sm font-medium hover:bg-black/5"
                  >
                    Newsletter
                  </NewsletterSignupLink>
                ) : (
                  <MarketingLink
                    key={`${link.label}:${link.href}`}
                    href={link.href}
                    className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-black/5"
                    onClick={() => setAccessOpen(false)}
                  >
                    {link.label}
                  </MarketingLink>
                )
              )}
              <div className="mt-1 border-t border-black/10 pt-1">
                <MemberAccessMenuItems
                  access={access}
                  memberHref={memberAccessHeld ? "/account" : "/dashboard"}
                  memberLabel={memberAccessHeld ? "Account" : "Dashboard"}
                  itemClassName="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-black/5"
                  onNavigate={() => setAccessOpen(false)}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
